import { getAccount, waitForTransactionReceipt, writeContract } from "@wagmi/core";
import { getWagmiConfig } from "./wallet/reown.js";
import { getStakingEnv } from "./lib/env.js";
import { stakingAbi } from "./lib/abis.js";
import {
  clearStakedTokenIdsCache,
  readStakingPaused,
  stakingAddressesOrNull,
} from "./lib/stakingData.js";
import { getSelectedWalletTokenIds } from "./nftGallery.js";
import { getSelectedRoostTokenIds } from "./roostGallery.js";
import { showToast } from "./lib/toast.js";
import { formatTxError } from "./lib/txErrors.js";
import { clearOwnedNftCache } from "./lib/alchemyNfts.js";

function dispatchRefresh(cfg) {
  const { address: a } = getAccount(cfg);
  clearOwnedNftCache(a ?? null);
  clearStakedTokenIdsCache(a ?? null);
  window.dispatchEvent(
    new CustomEvent("owlex:staking-refresh", { detail: { address: a } })
  );
}

async function writeStaking(fn, args, label) {
  const cfg = getWagmiConfig();
  if (!cfg) {
    showToast("Wallet not ready.", { variant: "info" });
    return;
  }
  const { address } = getAccount(cfg);
  if (!address) {
    showToast("Connect your wallet first.", { variant: "info" });
    return;
  }
  const { staking } = stakingAddressesOrNull();
  if (!staking) {
    showToast("Missing contract addresses in env.", { variant: "info" });
    return;
  }

  const btn = document.getElementById(
    label === "activate" ? "btn-stake" : label === "deactivate" ? "btn-unstake" : "btn-claim"
  );
  const prev = btn?.textContent;
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Checking…";
  }

  try {
    const paused = await readStakingPaused(cfg, staking);
    if (paused) {
      showToast("Staking is paused.", { variant: "info" });
      return;
    }
    if (btn) btn.textContent = "Confirm…";

    const hash = await writeContract(cfg, {
      address: staking,
      abi: stakingAbi,
      functionName: fn,
      args,
      chainId: 1,
    });
    await waitForTransactionReceipt(cfg, { hash });
  } catch (err) {
    console.error(`[owlex-staking] ${label}`, err);
    showToast(formatTxError(err), { variant: "error" });
  } finally {
    if (btn) btn.textContent = prev ?? btn.textContent;
    dispatchRefresh(cfg);
  }
}

/** Activate soft staking for selected wallet NFTs (NFT stays in wallet). */
export async function runStake() {
  const tokenIds = getSelectedWalletTokenIds();
  if (tokenIds.length === 0) {
    showToast("Select one or more Owlex in the Wallet tab.", { variant: "info" });
    return;
  }
  const tids = tokenIds.map((id) => BigInt(id));
  if (tids.length === 1) {
    await writeStaking("activate", [tids[0]], "activate");
  } else {
    await writeStaking("activateBatch", [tids], "activate");
  }
}

/** Deactivate selected active NFTs (claims pending XP first). */
export async function runUnstake() {
  const tokenIds = getSelectedRoostTokenIds();
  if (tokenIds.length === 0) {
    showToast("Select one or more Owlex in the Active tab.", { variant: "info" });
    return;
  }
  const tids = tokenIds.map((id) => BigInt(id));
  if (tids.length === 1) {
    await writeStaking("deactivate", [tids[0]], "deactivate");
  } else {
    await writeStaking("deactivateBatch", [tids], "deactivate");
  }
}

/** Claim accrued XP for selected active NFTs. */
export async function runClaim() {
  const tokenIds = getSelectedRoostTokenIds();
  if (tokenIds.length === 0) {
    showToast("Select active Owlex with claimable XP.", { variant: "info" });
    return;
  }

  for (const tokenId of tokenIds) {
    const card = document.querySelector(
      `.nft-card--roost[data-token-id="${tokenId}"]`
    );
    if (card?.getAttribute("data-can-claim") !== "true") {
      showToast("Wait until claim is ready for every selected NFT.", {
        variant: "info",
      });
      return;
    }
  }

  const tids = tokenIds.map((id) => BigInt(id));
  if (tids.length === 1) {
    await writeStaking("claim", [tids[0]], "claim");
  } else {
    await writeStaking("claimBatch", [tids], "claim");
  }
}
